import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { lazy, Suspense } from 'react';
import { chartSx } from './chart-style';

// Loaded on demand so the charts package stays out of the app shell bundle.
const LineChart = lazy(() =>
  import('@mui/x-charts/LineChart').then((m) => ({ default: m.LineChart })),
);

export interface TrendChartSeries {
  id: string;
  label: string;
  data: (number | null)[];
  color: string;
}

interface TrendChartProps {
  labels: string[];
  /** Optional date range per label, shown next to it in the tooltip header (e.g. a week's days). */
  dates?: (string | undefined)[];
  series: TrendChartSeries[];
  valueFormatter: (value: number | null) => string;
  /** Shorter labels for the y axis (defaults to `valueFormatter`). */
  axisFormatter?: (value: number) => string;
  height?: number;
  /** Filled area under a single series (one-location view). */
  area?: boolean;
}

/** Line chart for 13-week / 12-month trends, themed like the prototype. */
export function TrendChart({
  labels,
  dates,
  series,
  valueFormatter,
  axisFormatter,
  height = 300,
  area,
}: TrendChartProps) {
  return (
    <Box sx={[{ width: '100%' }, chartSx]}>
      <Suspense fallback={<Skeleton variant="rounded" height={height} />}>
        <LineChart
          height={height}
          // Taller than the 25px default: x-charts empties tick labels whose measured height
          // (Manrope at 12px) does not fit the axis.
          xAxis={[
            {
              scaleType: 'point',
              data: labels,
              height: 34,
              valueFormatter: (value: string, context) => {
                if (context.location !== 'tooltip' || !dates) return value;
                const date = dates[labels.indexOf(value)];
                return date ? `${value} · ${date}` : value;
              },
            },
          ]}
          yAxis={[
            {
              valueFormatter: (v: number | null) =>
                v === null ? '' : axisFormatter ? axisFormatter(v) : valueFormatter(v),
              width: 64,
            },
          ]}
          series={series.map((s) => ({
            id: s.id,
            label: s.label,
            data: s.data,
            color: s.color,
            curve: 'monotoneX',
            showMark: series.length === 1,
            area: area && series.length === 1,
            valueFormatter: (v: number | null) => valueFormatter(v),
          }))}
          grid={{ horizontal: true }}
          hideLegend={series.length < 2}
          margin={{ top: 16, right: 26, bottom: 8, left: 8 }}
        />
      </Suspense>
    </Box>
  );
}
